/**
 * ``useSectionVisibility`` — bridge from the preferences API to the
 * Entity Details panel's per-section **visibility** and **expand/
 * collapse** state, for every entity type.
 *
 * Stream A PR 5.4 (platform-only visibility) → Profiles increment 7
 * (all four entity types + the expand/collapse dimension). The
 * section defaults live at
 * ``working_state.entity_details.<entityType>.sections`` as
 * ``{ section_id, visible, expanded }[]`` per entity type (ADR-033
 * §10 — relocated out of the v2 ``panel_layout.details_panel_sections``
 * in the preferences-v3 flip, and enriched with ``expanded``). This
 * round-trips via the existing preferences PUT/GET surface.
 *
 * ``expanded`` realizes REQ-UI-064 (Entity Details sections are
 * collapsible *in addition to* hideable, persisted per user, applied
 * before first paint, for all entity types). The previously-flagged
 * api-change-request #358 (which asked for a ``collapsed`` flag on the
 * v2 ``DetailsPanelSection``) is **superseded**: ADR-033 §10 relocated
 * section config into v3 ``entity_details`` *with* the ``expanded``
 * field, so the persisted collapse state #358 wanted already exists —
 * no backend gap, no ACR.
 *
 * The hook is parameterized by ``entityType`` and exposes, for that
 * type only:
 *
 *   - ``isVisible(section_id)`` — boolean, default ``true`` for unknown
 *     ids per ADR-024 §6 / REQ-UI-092 ("unknown section ids default to
 *     visible+expanded").
 *   - ``setVisible(section_id, visible)`` — optimistically updates the
 *     working state via ``setWorkingState``. Returns a Promise mirroring
 *     :type:`SetPreferencesResult`. ``expanded`` is preserved across a
 *     visibility toggle (default ``true`` for a section the document
 *     doesn't yet carry).
 *   - ``isExpanded(section_id)`` — boolean, default ``true`` for unknown
 *     ids per REQ-UI-092. ``expanded === false`` ⇒ the section renders
 *     collapsed (the "show by default, others collapsed" semantics).
 *   - ``setExpanded(section_id, expanded)`` — optimistic; mirror of
 *     ``setVisible`` on the orthogonal dimension. ``visible`` is
 *     preserved across an expand toggle.
 *   - ``sectionIds`` — ordered list of the entity type's canonical
 *     section ids (for rendering the gear-menu checkbox list).
 *
 * Round-trip discipline (REQ-AUTH-060): a write to one entity type's
 * ``sections`` preserves the other types' ``entity_details``
 * sub-objects (and the rest of the working state) — the v3 working
 * state holds every sibling type key.
 *
 * **Seven types, two persistence models (REQ-UI-131).** The
 * ``EntityDetailsDefaults`` document gained ``status_component`` /
 * ``status_interface`` / ``status_connection`` (ADR-024 §4 amended
 * 2026-07-31), so :type:`EntityDetailType` and the tables below now
 * enumerate seven. The four original types persist a toggle immediately
 * through this hook; the three **status** kinds collapse session-locally
 * and are captured only when the operator saves a Profile — see the
 * "Status selection kinds" helpers at the foot of this module and
 * :func:`StatusSectionsProvider` (``system-status/status-detail-primitives``),
 * which is what actually drives them. Nothing calls
 * :func:`useSectionVisibility` with a status type.
 *
 * Cross-references:
 *   - REQ-UI-092 (per-type visibility + expand/collapse defaults;
 *     unknown ids default visible+expanded).
 *   - REQ-UI-064 (collapsible sections, persisted, all entity types).
 *   - REQ-UI-131 (status kinds — session-local, captured on Profile save).
 *   - REQ-UI-050 (section-visibility menu + persistence — the
 *     platform-only ancestor this generalizes).
 *   - REQ-AUTH-070 (v3 working_state — entity_details slice).
 *   - ADR-024 §4 + §6 (visibility menu + storage); ADR-033 §10.
 *   - Flow Q §"Frame 4" (gear menu UX).
 */
import { useMemo } from "react";
import { usePreferences } from "@/preferences/preferences-context";
import type { SetPreferencesResult } from "@/preferences/preferences-context";
import type { ProfileDocument } from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";

/** One persisted entity-detail section default (ADR-033 §10). Aliases
 *  the generated ``SectionDefault`` so the local helpers stay bound to
 *  the BFF wire shape (``{ section_id, visible, expanded }``). */
type SectionDefault = components["schemas"]["SectionDefault"];

/**
 * The entity types that carry an Entity Details section schema. Bound
 * to the generated ``EntityDetailsDefaults`` keys so adding a fifth
 * type on the BFF surfaces here at compile time rather than silently
 * diverging (ADR-033 §10).
 */
export type EntityDetailType = keyof components["schemas"]["EntityDetailsDefaults"];

/**
 * Canonical section ids per entity type. Order matches each panel's
 * own render order (ADR-024 §4); the gear menu lists sections in this
 * order. Any id absent from the persisted document defaults to
 * ``visible: true`` / ``expanded: true`` (REQ-UI-092).
 *
 * The ids match the panels' existing ``SectionShell id=`` values
 * verbatim (so a default can target a rendered shell by id).
 */
export const SECTION_IDS = {
  platform: [
    "status",
    "position",
    "registration",
    "connectivity",
    "recent_messages",
    "configuration",
    "recent_errors",
    // The registration message this producer was admitted on, as bytes
    // (REQ-UI-184). Last, like every kind's: it is the drill-down behind the
    // facts above, and it is the one section whose body is an audited
    // disclosure the operator opts into.
    "source_bytes",
  ],
  aggregator: [
    "status",
    "connectivity",
    "message_rates",
    "multiplexed_platforms",
    "configuration",
    "recent_errors",
    "source_bytes",
  ],
  detection: [
    "identity",
    "classification",
    "track_object",
    "geometry",
    "signal",
    "behaviour",
    "associations",
    "detected_by",
    "provenance",
    "source_bytes",
  ],
  alert: ["alert", "location", "region", "associations", "source", "dismissal", "source_bytes"],
  // The object-track kind (ADR-041 D-5; REQ-UI-174). The BFF's
  // ``EntityDetailsDefaults.track`` defaults to ``None`` (additive against
  // deployed documents), so a Profile without it reads as every section
  // visible + expanded — the seeded default the four kinds above carry.
  track: [
    "identity",
    "summary",
    "head",
    "geometry",
    "detections",
    "also_reported_by",
    "provenance",
    // Present although a track HAS no wire bytes: the section states why and
    // points at its members, because on a pane whose sibling kinds all carry
    // bytes an absent section reads as a fault (CLAUDE.md §8.1; REQ-UI-184).
    "source_bytes",
  ],
  // The System panel's three selection kinds (ADR-024 §4 amended
  // 2026-07-31; REQ-STATUS-039 added the interface level). These collapse
  // SESSION-LOCALLY (REQ-UI-131) — ``useSectionVisibility`` is not the
  // driver for them; :func:`StatusSectionsProvider` is. The ids live here
  // anyway so the roster is declared exactly once, and so a Profile save
  // (which does persist them) and the Profile editor read the same table.
  status_component: [
    "instances",
    "status-interfaces",
    "status-health",
    "status-resource",
    "status-domain",
    "status-connections",
    "status-trend",
  ],
  status_interface: ["interface-identity", "interface-connections-summary"],
  status_connection: ["conn-health", "conn-throughput", "conn-errors", "conn-history"],
} as const satisfies Record<EntityDetailType, readonly string[]>;

/**
 * Human-readable section titles per entity type, used for the gear-menu
 * checkbox labels. Sourced from each panel's existing ``SectionShell
 * title=`` props verbatim so the menu label matches the rendered
 * header. Unknown ids fall back to the raw id at the call site.
 */
export const SECTION_TITLES = {
  platform: {
    status: "Status",
    position: "Position",
    registration: "Registration",
    connectivity: "Connectivity",
    recent_messages: "Recent messages",
    configuration: "Configuration",
    recent_errors: "Recent errors",
    source_bytes: "Source bytes",
  },
  aggregator: {
    status: "Status",
    connectivity: "Connectivity",
    message_rates: "Message rates",
    multiplexed_platforms: "Multiplexed platforms",
    configuration: "Configuration",
    recent_errors: "Recent errors",
    source_bytes: "Source bytes",
  },
  detection: {
    identity: "Identity",
    classification: "Classification",
    track_object: "Track / object info",
    geometry: "Geometry",
    signal: "Signal",
    behaviour: "Behaviour",
    associations: "Associations",
    detected_by: "Detected by",
    provenance: "Provenance",
    source_bytes: "Source bytes",
  },
  alert: {
    alert: "Alert",
    location: "Location",
    region: "Region",
    associations: "Associations",
    source: "Source",
    dismissal: "Dismissal",
    source_bytes: "Source bytes",
  },
  track: {
    identity: "Identity",
    summary: "Summary",
    head: "Head",
    geometry: "Geometry",
    detections: "Detections",
    also_reported_by: "Also reported by",
    provenance: "Provenance",
    source_bytes: "Source bytes",
  },
  // Verbatim from the status panes' ``SectionShell title=`` props
  // (entity-details/status-{component,interface,connection}-sections.tsx),
  // same discipline as the four types above.
  status_component: {
    instances: "Reporting components",
    "status-interfaces": "Interfaces",
    "status-health": "Health rollup",
    "status-resource": "Resource utilization",
    "status-domain": "Domain metrics",
    "status-connections": "Connections",
    "status-trend": "Trend",
  },
  status_interface: {
    "interface-identity": "Interface",
    "interface-connections-summary": "Connection roll-up",
  },
  status_connection: {
    "conn-health": "Health rollup",
    "conn-throughput": "Throughput",
    "conn-errors": "Errors",
    "conn-history": "Connection history",
  },
} as const satisfies Record<EntityDetailType, Record<string, string>>;

/**
 * Back-compat alias for the platform section-id tuple. The platform
 * surface (section-visibility-menu + platform-sections) and its tests
 * referenced ``PLATFORM_SECTION_IDS`` before increment 7 generalized
 * the maps; keep the named export so those call sites and the standing
 * ordering test continue to resolve.
 */
export const PLATFORM_SECTION_IDS = SECTION_IDS.platform;

/** Back-compat alias for the platform section titles (see
 *  :const:`PLATFORM_SECTION_IDS`). */
export const PLATFORM_SECTION_TITLES = SECTION_TITLES.platform;

export interface UseSectionVisibilityResult {
  /** Test ``section_id`` against the visibility table. Unknown ids
   *  default to ``true`` per ADR-024 §6 / REQ-UI-092. */
  isVisible: (sectionId: string) => boolean;
  /** Update one section's visibility. Optimistic — same semantics as
   *  the rest of the preferences API. ``expanded`` is preserved. */
  setVisible: (sectionId: string, visible: boolean) => Promise<SetPreferencesResult>;
  /** Test ``section_id`` against the expand table. Unknown ids default
   *  to ``true`` (expanded) per REQ-UI-092. */
  isExpanded: (sectionId: string) => boolean;
  /** Update one section's expand/collapse state. Optimistic; mirror of
   *  :func:`setVisible` on the orthogonal dimension. ``visible`` is
   *  preserved. */
  setExpanded: (sectionId: string, expanded: boolean) => Promise<SetPreferencesResult>;
  /** Canonical section order for rendering the gear menu. */
  sectionIds: readonly string[];
}

/**
 * Bridge the preferences context to the section-visibility +
 * expand/collapse surface for one entity type.
 *
 * The implementation is intentionally boring: read the section-defaults
 * array from ``working_state.entity_details.<entityType>.sections``,
 * build Maps for O(1) lookup, and return mutators that re-assemble the
 * array and PUT the working state. No additional state — the working
 * state is the single source of truth.
 *
 * @param entityType - which entity type's section defaults to read/write.
 */
export function useSectionVisibility(entityType: EntityDetailType): UseSectionVisibilityResult {
  const { workingState, setWorkingState } = usePreferences();

  const { visibility, expansion } = useMemo(() => {
    return extractMaps(workingState, entityType);
  }, [workingState, entityType]);

  // Both setters share the round-trip: read the current array, upsert
  // the one field, and re-assemble the working state preserving the
  // other three entity types (and the rest of the document).
  const persist = (next: SectionDefault[]): Promise<SetPreferencesResult> => {
    return setWorkingState({
      entity_details: {
        ...workingState.entity_details,
        [entityType]: {
          ...workingState.entity_details?.[entityType],
          sections: next,
        },
      },
    });
  };

  const setVisible = async (sectionId: string, visible: boolean): Promise<SetPreferencesResult> => {
    const current = extractSectionsArray(workingState, entityType);
    return persist(upsertSectionField(current, sectionId, { visible }));
  };

  const setExpanded = async (
    sectionId: string,
    expanded: boolean,
  ): Promise<SetPreferencesResult> => {
    const current = extractSectionsArray(workingState, entityType);
    return persist(upsertSectionField(current, sectionId, { expanded }));
  };

  return {
    isVisible: (sectionId: string) => {
      const v = visibility.get(sectionId);
      // Per ADR-024 §6 / REQ-UI-092 — unknown sections default to visible.
      return v === undefined ? true : v;
    },
    setVisible,
    isExpanded: (sectionId: string) => {
      const e = expansion.get(sectionId);
      // Per REQ-UI-092 — unknown sections default to expanded.
      return e === undefined ? true : e;
    },
    setExpanded,
    sectionIds: SECTION_IDS[entityType],
  };
}

// ---- Helpers ----------------------------------------------------

/** Build the visibility + expansion lookup Maps in one pass. */
function extractMaps(
  doc: ProfileDocument,
  entityType: EntityDetailType,
): { visibility: Map<string, boolean>; expansion: Map<string, boolean> } {
  const sections = extractSectionsArray(doc, entityType);
  const visibility = new Map<string, boolean>();
  const expansion = new Map<string, boolean>();
  for (const section of sections) {
    visibility.set(section.section_id, section.visible);
    expansion.set(section.section_id, section.expanded);
  }
  return { visibility, expansion };
}

function extractSectionsArray(
  doc: ProfileDocument,
  entityType: EntityDetailType,
): SectionDefault[] {
  return sectionsFromEntityDetails(doc.entity_details, entityType);
}

/** The well-formed ``sections`` array for one type, read off an
 *  ``entity_details`` sub-document (which may be absent, or carry the key
 *  as ``null`` — the status kinds default to ``None`` on the BFF). */
function sectionsFromEntityDetails(
  entityDetails: ProfileDocument["entity_details"],
  entityType: EntityDetailType,
): SectionDefault[] {
  const sections = entityDetails?.[entityType]?.sections;
  if (!Array.isArray(sections)) {
    return [];
  }
  return sections.filter(isSectionEntry);
}

function isSectionEntry(value: unknown): value is SectionDefault {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.section_id === "string" &&
    typeof obj.visible === "boolean" &&
    typeof obj.expanded === "boolean"
  );
}

/**
 * Upsert one section default, patching only the supplied field(s) and
 * preserving the other. For a brand-new entry the unpatched field
 * defaults to ``true`` (the "show + expand by default" semantics per
 * ADR-033 §10 / REQ-UI-092). Visibility and expand are independent
 * dimensions, so a write to one never disturbs the other.
 */
function upsertSectionField(
  current: SectionDefault[],
  sectionId: string,
  patch: Partial<Pick<SectionDefault, "visible" | "expanded">>,
): SectionDefault[] {
  const existingIndex = current.findIndex((s) => s.section_id === sectionId);
  if (existingIndex >= 0) {
    const next = current.slice();
    next[existingIndex] = { ...current[existingIndex], ...patch };
    return next;
  }
  return [...current, { section_id: sectionId, visible: true, expanded: true, ...patch }];
}

// ---- Status selection kinds (REQ-UI-131) ------------------------
//
// The three System-panel kinds are entity types on the wire (the additive
// ``EntityDetailsDefaults`` fields — ADR-024 §4 amended 2026-07-31) but
// they do NOT persist per toggle like the four above. Their collapse is
// session-local, and a **Profile save** is what captures it (REQ-UI-131).
// That splits the work in two, and both halves live here so the wire
// shape is written in exactly one place:
//
//   - :func:`statusSectionExpansionFromDocument` — the *read*: the
//     baseline a mounted status pane starts from (the active Profile's
//     captured states, or nothing).
//   - :func:`foldStatusSectionOverrides` — the *write*: the session's
//     toggles folded into the document a Profile save persists.
//
// Between them sits a small module-level registry of the mounted stores'
// session overrides, because the save path is imperative (a click reads
// the live config) and cannot subscribe to a React store it does not own.

/** The three status selection kinds, as :type:`EntityDetailType` keys. */
export type StatusEntityDetailType = Extract<EntityDetailType, `status_${string}`>;

/** The status kinds in canonical order (component → interface →
 *  connection, the 2026-07-31 hierarchy). Iterated wherever all three are
 *  handled, so a fourth kind is a one-line addition. */
export const STATUS_ENTITY_DETAIL_TYPES = [
  "status_component",
  "status_interface",
  "status_connection",
] as const satisfies readonly StatusEntityDetailType[];

/**
 * The Profile-captured expand/collapse baseline for the status kinds:
 * ``section_id → expanded``, merged across all three (their section ids
 * are disjoint by construction — asserted in the tests).
 *
 * A section id absent from the result has no captured state and resolves
 * to **expanded** at the call site (REQ-UI-092 — never hidden, never
 * collapsed by omission).
 *
 * @param doc - The live working state (i.e. the applied Profile), or
 *     ``undefined`` when no preferences provider is mounted.
 */
export function statusSectionExpansionFromDocument(
  doc: ProfileDocument | undefined,
): ReadonlyMap<string, boolean> {
  const expansion = new Map<string, boolean>();
  if (doc === undefined) return expansion;
  for (const type of STATUS_ENTITY_DETAIL_TYPES) {
    for (const section of sectionsFromEntityDetails(doc.entity_details, type)) {
      expansion.set(section.section_id, section.expanded);
    }
  }
  return expansion;
}

/**
 * Fold the session's status collapse states into an ``entity_details``
 * document — the "captured into that Profile" half of REQ-UI-131.
 *
 * Called only from the **explicit save/update capture**
 * (:func:`buildLiveDocument`), never from a toggle: collapsing a section
 * mutates session state and nothing else. Each override upserts only the
 * ``expanded`` dimension of its section's entry, so a visibility default
 * an admin set on the Profile survives an operator's triage collapse.
 *
 * Returns ``entityDetails`` **by reference** when there is nothing to fold,
 * so a save from a session that never touched a status section produces a
 * byte-identical document (no false "(modified)"; no needless write).
 *
 * @param entityDetails - The document's current ``entity_details``.
 * @param overrides - ``section_id → expanded`` for the sections toggled
 *     this session (see :func:`getStatusSectionOverrides`).
 */
export function foldStatusSectionOverrides(
  entityDetails: ProfileDocument["entity_details"],
  overrides: ReadonlyMap<string, boolean>,
): ProfileDocument["entity_details"] {
  if (overrides.size === 0) return entityDetails;
  let next = entityDetails;
  for (const type of STATUS_ENTITY_DETAIL_TYPES) {
    // Iterate the CANONICAL id order (not the override map's insertion
    // order) so the persisted array is deterministic — two sessions that
    // collapse the same sections in a different order hash identically
    // (REQ-UI-074's "(modified)" baseline compares canonical JSON).
    let sections: SectionDefault[] | null = null;
    for (const sectionId of SECTION_IDS[type]) {
      const expanded = overrides.get(sectionId);
      if (expanded === undefined) continue;
      sections ??= sectionsFromEntityDetails(next, type);
      sections = upsertSectionField(sections, sectionId, { expanded });
    }
    if (sections === null) continue;
    next = { ...next, [type]: { ...(next?.[type] ?? {}), sections } };
  }
  return next;
}

/**
 * Session collapse states published by the mounted status panes, keyed by
 * store id. Module-level because the save path is **imperative**: a click
 * on "Save as new Profile" reads the live configuration
 * (:func:`buildSaveDocument`) from a component that is not an ancestor of
 * the System panel and cannot hold its React state.
 *
 * Deliberately NOT the store itself — each :func:`StatusSectionsProvider`
 * keeps its own ``useState`` (so a pane's arrangement is per-mount, and so
 * unit tests stay isolated) and merely *publishes* a snapshot here.
 * Entries are retracted on unmount, so a closed panel contributes nothing
 * to the next save.
 */
const liveStatusSectionOverrides = new Map<number, ReadonlyMap<string, boolean>>();

/** Monotonic store-id source for :func:`publishStatusSectionOverrides`. */
let nextStatusSectionStoreId = 0;

/** Claim a store id for one mounted status-sections store. */
export function claimStatusSectionStoreId(): number {
  nextStatusSectionStoreId += 1;
  return nextStatusSectionStoreId;
}

/** Publish one mounted store's session overrides (idempotent per id). */
export function publishStatusSectionOverrides(
  storeId: number,
  overrides: ReadonlyMap<string, boolean>,
): void {
  liveStatusSectionOverrides.set(storeId, overrides);
}

/** Withdraw a store's overrides — called on unmount. */
export function retractStatusSectionOverrides(storeId: number): void {
  liveStatusSectionOverrides.delete(storeId);
}

/**
 * The merged session overrides across every mounted status pane, for the
 * save-time fold. Later-mounted stores win on a shared id; in practice
 * ids never collide across panes because each kind owns a disjoint set
 * and a nested pane defers to its host's store rather than opening a
 * second one.
 */
export function getStatusSectionOverrides(): ReadonlyMap<string, boolean> {
  if (liveStatusSectionOverrides.size === 1) {
    // The overwhelmingly common case (one System panel): hand back the
    // published map rather than copying it on every save/dirty-check.
    return [...liveStatusSectionOverrides.values()][0];
  }
  const merged = new Map<string, boolean>();
  for (const overrides of liveStatusSectionOverrides.values()) {
    for (const [sectionId, expanded] of overrides) merged.set(sectionId, expanded);
  }
  return merged;
}
